"use client"

import { useAuth } from "@clerk/nextjs"
import { useCallback, useEffect, useState } from "react"

import { fetchMyMedia, type MyMemoryMedia } from "@/lib/api/media"
import { orderMemoriesGalleryWithHeroClusters } from "@/lib/media/memories-gallery-hero-order"

export const MEMORIES_REFRESH_EVENT = "maatii:memories-refresh" as const

async function loadMemoriesForUser(
  signal: AbortSignal,
  clerkLoaded: boolean,
  userId: string | null | undefined,
  setItems: (items: MyMemoryMedia[]) => void,
  setFetchDone: (done: boolean) => void,
): Promise<void> {
  if (!clerkLoaded) return
  const uid = userId?.trim()
  if (!uid) {
    setItems([])
    setFetchDone(true)
    return
  }
  setFetchDone(false)
  try {
    const data = await fetchMyMedia({ signal })
    if (!signal.aborted) {
      const normalized = data.map((d) => ({
        ...d,
        userId: d.userId?.trim() || d.uploadedByUserId,
        eventOwnerId:
          d.eventOwnerId?.trim() || d.eventCreatedByUserId?.trim() || "",
        createdAt: new Date(d.createdAt),
      }))
      setItems(
        orderMemoriesGalleryWithHeroClusters(normalized).map((row) => ({
          ...row,
          createdAt: row.createdAt.toISOString(),
        })),
      )
    }
  } catch (e: unknown) {
    if (e instanceof Error && e.name === "AbortError") return
    if (!signal.aborted) setItems([])
  } finally {
    if (!signal.aborted) setFetchDone(true)
  }
}

export function useMemories() {
  const { userId, isLoaded: clerkLoaded } = useAuth()
  const [items, setItems] = useState<MyMemoryMedia[]>([])
  const [fetchDone, setFetchDone] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    void loadMemoriesForUser(
      ac.signal,
      clerkLoaded,
      userId,
      setItems,
      setFetchDone,
    )
    return () => ac.abort()
  }, [clerkLoaded, userId])

  useEffect(() => {
    const onRefresh = () => {
      const ac = new AbortController()
      void loadMemoriesForUser(
        ac.signal,
        clerkLoaded,
        userId,
        setItems,
        setFetchDone,
      )
    }
    window.addEventListener(MEMORIES_REFRESH_EVENT, onRefresh)
    return () => window.removeEventListener(MEMORIES_REFRESH_EVENT, onRefresh)
  }, [clerkLoaded, userId])

  const refresh = useCallback(async () => {
    const ac = new AbortController()
    await loadMemoriesForUser(
      ac.signal,
      clerkLoaded,
      userId,
      setItems,
      setFetchDone,
    )
  }, [clerkLoaded, userId])

  const removeMemoryMedia = useCallback((mediaId: string) => {
    const id = mediaId.trim()
    if (!id) return
    setItems((prev) => prev.filter((x) => x.id !== id))
  }, [])

  const insertMemoryMediaAt = useCallback(
    (item: MyMemoryMedia, index: number) => {
      setItems((prev) => {
        const i = Math.max(0, Math.min(index, prev.length))
        const next = [...prev]
        next.splice(i, 0, item)
        return next
      })
    },
    [],
  )

  return {
    items,
    isLoaded: clerkLoaded && fetchDone,
    refresh,
    removeMemoryMedia,
    insertMemoryMediaAt,
  }
}
